import '@/i18n/config';
import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { initInternalAnalytics } from '@/landing/internalAnalytics';
import { initTelemetry } from '@/lib/analytics/telemetry';
import { RouteTelemetry } from '@/lib/analytics/RouteTelemetry';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from './ErrorBoundary';
import { ConditionalAuthProvider } from '@/auth/ConditionalAuthProvider';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es-mx';
import i18next from 'i18next';
import { AppProvider, AppToastProvider } from '@/components';
import { App } from './App';

dayjs.extend(relativeTime);

// Initialize internal analytics client for admin funnel tracking.
// The landing page re-initialises it after consent; this ensures the client
// is ready for admin-only paths (e.g. onboarding wizard) that never hit the
// landing consent flow.
const _apiUrl = import.meta.env.VITE_API_URL || '';
const _ingestUrl = _apiUrl
  ? `${_apiUrl}/api/public/analytics/events`
  : '/api/public/analytics/events';
initInternalAnalytics(_ingestUrl);

// Initialize ambient-telemetry for the authenticated product (Umami events +
// GlitchTip errors) — separate from the internal marketing tracker above.
initTelemetry();

// Keep dayjs locale in sync with i18next language
const syncDayjsLocale = (lang: string) => {
  dayjs.locale(lang === 'es-MX' ? 'es-mx' : 'en');
};
syncDayjsLocale(i18next.language);
i18next.on('languageChanged', syncDayjsLocale);

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

const app = (
  <ErrorBoundary>
    <StrictMode>
      <HelmetProvider>
        <BrowserRouter>
          <RouteTelemetry />
          <ConditionalAuthProvider>
            <AppProvider>
              <AppToastProvider>
                <App />
              </AppToastProvider>
            </AppProvider>
          </ConditionalAuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </StrictMode>
  </ErrorBoundary>
);

// If the root already has children (pre-rendered landing page) AND we're on
// the landing route, hydrate to attach event handlers without discarding HTML.
// Otherwise do a normal client render.
if (root.childNodes.length > 0 && window.location.pathname === '/') {
  hydrateRoot(root, app);
} else {
  // Clear any pre-rendered content when navigating directly to a non-landing
  // route (e.g. /admin). Without this, React would try to reconcile the
  // landing HTML with the app tree and produce hydration mismatches.
  root.innerHTML = '';
  createRoot(root).render(app);
}
